import { useAddOnFilter } from '@gripp/shared-logic';
import { Colors, BaseColumnCheckbox } from '@gripp/shared-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text } from 'react-native';
import { AddonAssetsAssignmentButton } from './addonAssetsAssignmentButton';
import { AddonMediaAssignmentButton } from './addonMediaAssignmentButton';
import { AddonProductAssignmentButton } from './addonProductAssignmentButton';
import { AddonRoutinesAssignmentButton } from './addonRoutinesAssignmentButton';

export const useColumnsAddOn = (showCheckboxColumn: boolean) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<any>();

  const { data: addOnFilter } = useAddOnFilter();

  const [columns, setColumns] = useState<ColumnDef<any, any>[]>([]);

  const allColumns: Record<string, ColumnDef<any, any>> = useMemo(
    () => ({
      selected: columnHelper.accessor('selected', {
        cell: BaseColumnCheckbox.checkbox,
        header: BaseColumnCheckbox.checkboxHeader,
        meta: { style: styles.checkboxCell },
      }),
      name: columnHelper.accessor('name', {
        cell: (info) => {
          const name = info.getValue();
          return <Text style={styles.cellText}>{name}</Text>;
        },
        header: () => t('addOn.list.name'),
        meta: { sortable: true },
      }),
      product: columnHelper.accessor('productsCount', {
        cell: (info) => {
          const row = info.row.original as any;
          const productCount = info.getValue() as number || 0;
          return (
            <AddonProductAssignmentButton
              addonId={row.id}
              addonName={row.name}
              productCount={productCount}
            />
          );
        },
        header: () => t('addOn.list.product'),
        meta: { sortable: true },
      }),
      asset: columnHelper.accessor('assetsCount', {
        cell: (info) => {
          const row = info.row.original as any;
          const assetsCount = info.getValue() as number || 0;
          return (
            <AddonAssetsAssignmentButton
              addonId={row.id}
              addonName={row.name}
              assetsCount={assetsCount}
            />
          );
        },
        header: () => t('addOn.list.asset'),
        meta: { sortable: true },
      }),
      media: columnHelper.accessor('mediaCount', {
        cell: (info) => {
          const row = info.row.original as any;
          const mediaCount = info.getValue() as number || 0;
          return (
            <AddonMediaAssignmentButton
              addonId={row.id}
              addonName={row.name}
              mediaCount={mediaCount}
            />
          );
        },
        header: () => t('addOn.list.media'),
        meta: { sortable: true },
      }),
      routines: columnHelper.accessor('routinesCount', {
        cell: (info) => {
          const row = info.row.original as any;
          const routinesCount = info.getValue() as number || 0;
          return (
            <AddonRoutinesAssignmentButton
              addonId={row.id}
              addonName={row.name}
              routinesCount={routinesCount}
            />
          );
        },
        header: () => t('addOn.list.routines'),
        meta: { sortable: true },
      }),
      createdAt: columnHelper.accessor('createdAt', {
        cell: (info) => {
          const createdAt = info.getValue();
          return (
            <Text style={styles.cellText}>
              {createdAt ? new Date(createdAt).toLocaleDateString() : '-'}
            </Text>
          );
        },
        header: () => t('addOn.list.createdAt'),
        meta: { sortable: true },
      }),
      updatedAt: columnHelper.accessor('updatedAt', {
        cell: (info) => {
          const updatedAt = info.getValue();
          return (
            <Text style={styles.cellText}>
              {updatedAt ? new Date(updatedAt).toLocaleDateString() : '-'}
            </Text>
          );
        },
        header: () => t('addOn.list.updatedAt'),
        meta: { sortable: true },
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addOnFilter]
  );

  useEffect(() => {
    if (!addOnFilter) return;

    const columnList = [];
    if (showCheckboxColumn) {
      columnList.push(allColumns.selected);
    }

    columnList.push(allColumns.name);

    // Add media column right after name if it's enabled
    if (addOnFilter.columns?.includes('media')) {
      columnList.push(allColumns.media);
    }

    // Add other columns (excluding media since it's already added, and name
    // which is unconditionally pushed above).
    Object.keys(allColumns)
      .filter(
        (key) =>
          key !== 'media' &&
          key !== 'name' &&
          addOnFilter.columns?.includes(key)
      )
      .forEach((key) => columnList.push(allColumns[key]));

    setColumns(columnList);
  }, [addOnFilter, allColumns, showCheckboxColumn]);

  return columns;
};

const styles = StyleSheet.create({
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
    color: Colors.role.textStrong,
  },
  emailText: {
    overflow: 'hidden',
  },
  header: { flexDirection: 'row', alignItems: 'center' },
  icon: {
    marginRight: 6,
  },
});
